/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useEffect, forwardRef } from 'react';
import { jsx } from '@emotion/react';
import { colors } from 'consts';

const ControlledInput = forwardRef(({
  value,
  onChange = null,
  onPressEnter = null,
  placeholder = '',
  style,
  type = 'text',
  disabled = false,
  autoFocus = false,
}, ref) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const handleChange = (e) => {
    const rawValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    if (onChange) onChange(rawValue, e);

    window.requestAnimationFrame(() => {
      if (ref.current) ref.current.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13 && onPressEnter) onPressEnter(e.target.value);
  };

  const inputCSS = Object.assign({}, {
    appearance: 'none',
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    borderRadius: '5px',
    border: '1px solid #DDDDDD',
    boxShadow: 'none',
    boxSizing: 'border-box',
    color: 'inherit',
    display: 'inline-block',
    fontFamily: `"Nebula Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
    fontSize: 'inherit',
    fontFeatureSettings: '"tnum"',
    fontVariant: 'tabular-nums',
    height: '100%',
    lineHeight: '1.5',
    listStyle: 'none',
    margin: '0',
    padding: '4px 12px',
    position: 'relative',
    touchAction: 'manipulation',
    transition: 'all .3s',
    width: '100%',
    outline: 'none',
    '&::placeholder': {
      color: 'rgb(128, 137, 142, 0.3)'
    },
    '&:focus': {
      border: `1px solid ${colors.standard.blue}`,
      boxShadow: `0px 0px 5px ${colors.standard.blue}`,
      '&::placeholder': {
        color: 'hsl(201, 50%, 87%)'
      },
    },
    ...style,
  });

  return (
    <input
      ref={ref}
      type={type}
      placeholder={placeholder}
      css={inputCSS}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      value={inputValue}
      disabled={disabled}
      autoFocus={autoFocus}
    />
  );
});

export default ControlledInput;
